import { useEffect, useState } from 'react';
import apiClient from '@/lib/api/client';

// ── Public-site change queue (CR #149) ──────────────────────────────────────
// The Site Errors panel could already turn a console error into a change
// request. This extends the same queuing to *anything* on ochrepoint.ca:
// compose an update / change / audit request against the marketing site, send
// it into the same queue Claude works, and track it through to its changelog.
//
// Public-site requests are tagged with page = 'ochrepoint.ca' so they filter
// cleanly apart from ops-app requests (which use in-app page names).

const PUBLIC_PAGE = 'ochrepoint.ca';

interface PublicCR {
  id: number; title: string; page: string; element?: string | null; details?: string | null;
  status: string; priority: string; source: string; fulfill_now: number;
  changelog?: string | null; created_at: string; fulfilled_at?: string | null;
  created_by_name?: string | null;
}

// Error-derived requests filed before the page tag existed still start with
// this title prefix — keep them in the view so the history stays complete.
const isPublicSiteRequest = (r: PublicCR) =>
  r.page === PUBLIC_PAGE || /^Fix ochrepoint\.ca error:/i.test(r.title || '');

const STATUS_ORDER = ['in_progress', 'open', 'proposed', 'done', 'rejected'];
const STATUS_META: Record<string, { label: string; cls: string; icon: string }> = {
  in_progress: { label: 'In progress', cls: 'bg-blue-100 text-blue-700', icon: 'progress_activity' },
  open:        { label: 'Queued',      cls: 'bg-amber-100 text-amber-800', icon: 'schedule' },
  proposed:    { label: 'Proposed',    cls: 'bg-purple-100 text-purple-700', icon: 'lightbulb' },
  done:        { label: 'Done',        cls: 'bg-green-100 text-green-700', icon: 'check_circle' },
  rejected:    { label: 'Rejected',    cls: 'bg-gray-100 text-gray-500', icon: 'cancel' },
};

// What part of the marketing site the request is about — stored in `element`
// so the agent knows where to look without parsing the prose.
const AREAS = [
  'Whole site', 'Home page', 'Services', 'Projects / gallery', 'About', 'Contact / quote form',
  'Blog', 'Navigation / footer', 'SEO & metadata', 'Performance', 'Mobile layout', 'Analytics tracking',
];

const KINDS = [
  { k: 'Update', hint: 'Change copy, photos, prices or details that are out of date.' },
  { k: 'Change', hint: 'Add, remove or rework something on the site.' },
  { k: 'Audit',  hint: 'Go look for problems in an area and report back with fixes.' },
];

export function PublicSiteQueue() {
  const [reqs, setReqs] = useState<PublicCR[]>([]);
  const [show, setShow] = useState<'active' | 'done' | 'all'>('active');
  const [q, setQ] = useState('');
  const [openId, setOpenId] = useState<number | null>(null);

  // Composer
  const [composing, setComposing] = useState(false);
  const [kind, setKind] = useState('Update');
  const [area, setArea] = useState(AREAS[0]);
  const [title, setTitle] = useState('');
  const [details, setDetails] = useState('');
  const [priority, setPriority] = useState('normal');
  const [now, setNow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState('');
  const [err, setErr] = useState('');

  const load = () =>
    apiClient.get('/dev/change-requests')
      .then(r => setReqs((r.data.data ?? []).filter(isPublicSiteRequest)))
      .catch(() => {});
  useEffect(() => { load(); const id = setInterval(load, 20000); return () => clearInterval(id); }, []);

  const submit = async () => {
    if (!title.trim()) return;
    setBusy(true); setErr(''); setMsg('');
    try {
      const r = await apiClient.post('/dev/change-requests', {
        page: PUBLIC_PAGE,
        element: area,
        title: `[${kind}] ${title.trim()}`,
        details: `${details.trim()}\n\nTarget: https://ochrepoint.ca — area: ${area}. Request type: ${kind.toLowerCase()}.\n(Filed from the public-site queue on /site-analytics.)`.trim(),
        priority,
        fulfill_now: now,
      });
      const id = r.data?.data?.id;
      setMsg(`Queued${id ? ` as #${id}` : ''}${now ? ' — flagged to run now.' : '.'}`);
      setTitle(''); setDetails(''); setNow(false); setPriority('normal'); setComposing(false);
      load();
      setTimeout(() => setMsg(''), 8000);
    } catch (e) {
      const ax = e as { response?: { data?: { error?: string } } };
      setErr(ax.response?.data?.error || 'Could not file that request.');
    } finally { setBusy(false); }
  };

  const setStatus = async (id: number, status: string) => {
    await apiClient.put(`/dev/change-requests/${id}`, { status }).catch(() => {});
    load();
  };
  const runNow = async (id: number) => {
    await apiClient.put(`/dev/change-requests/${id}`, { fulfill_now: true, priority: 'high' }).catch(() => {});
    load();
  };

  const active = reqs.filter(r => ['in_progress', 'open', 'proposed'].includes(r.status));
  const doneCount = reqs.filter(r => r.status === 'done').length;
  const needle = q.trim().toLowerCase();
  const matches = (r: PublicCR) => !needle ||
    [r.title, r.element, r.details, r.changelog].some(v => String(v || '').toLowerCase().includes(needle));
  const visible = (show === 'active' ? active : show === 'done' ? reqs.filter(r => r.status === 'done') : reqs)
    .filter(matches);
  const grouped = STATUS_ORDER
    .map(st => ({ st, rows: visible.filter(r => r.status === st) }))
    .filter(g => g.rows.length);

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-2 flex-wrap mb-3">
        <div>
          <h3 className="text-sm font-bold text-gray-900 flex items-center gap-1.5">
            <span className="material-symbols-outlined text-[#C77D3F] text-lg">public</span>
            ochrepoint.ca — update / change / audit queue
          </h3>
          <p className="text-[11px] text-gray-400">
            {active.length} queued · {doneCount} completed · {reqs.length} lifetime — same queue Claude works for the ops app,
            filed against the public marketing site.
          </p>
        </div>
        <button onClick={() => { setComposing(c => !c); setErr(''); }}
          className="h-8 px-3 rounded-lg bg-[#C77D3F] text-white text-[11px] font-bold whitespace-nowrap">
          {composing ? 'Cancel' : '+ New request'}
        </button>
      </div>

      {msg && <p className="text-[11px] text-green-700 mb-2">{msg}</p>}

      {composing && (
        <div className="border border-[#C77D3F]/30 rounded-lg p-3 mb-3 space-y-2">
          <div className="flex gap-1.5 flex-wrap">
            {KINDS.map(k => (
              <button key={k.k} onClick={() => setKind(k.k)} title={k.hint}
                className={`h-7 px-3 rounded-full text-[11px] font-bold border ${kind === k.k ? 'bg-[#2C1810] text-white border-[#2C1810]' : 'border-gray-200 text-gray-500'}`}>
                {k.k}
              </button>
            ))}
            <span className="text-[11px] text-gray-400 self-center">{KINDS.find(k => k.k === kind)?.hint}</span>
          </div>
          <div className="flex gap-1.5 flex-wrap">
            <input value={title} onChange={e => setTitle(e.target.value)}
              placeholder="What needs doing? (e.g. Refresh the Services page photos)"
              className="flex-1 min-w-[220px] border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#C77D3F]" />
            <select value={area} onChange={e => setArea(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
              {AREAS.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
            <select value={priority} onChange={e => setPriority(e.target.value)}
              className="border border-gray-300 rounded-lg px-2 py-1.5 text-xs bg-white focus:outline-none">
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>
          <textarea value={details} onChange={e => setDetails(e.target.value)} rows={3}
            placeholder="Details — exact wording, which page/section, what's wrong, what good looks like."
            className="w-full border border-gray-300 rounded-lg px-2 py-1.5 text-xs focus:outline-none focus:border-[#C77D3F] resize-y" />
          {err && <p className="text-[11px] text-red-600">{err}</p>}
          <div className="flex items-center gap-3 flex-wrap">
            <button onClick={submit} disabled={busy || !title.trim()}
              className="h-8 px-3 rounded-lg bg-[#2C1810] text-white text-xs font-bold disabled:opacity-40">
              {busy ? 'Filing…' : 'Queue it'}
            </button>
            <label className="flex items-center gap-1.5 text-[11px] text-gray-600">
              <input type="checkbox" checked={now} onChange={e => setNow(e.target.checked)} className="accent-[#C77D3F]" />
              Fulfill now (jump the queue)
            </label>
          </div>
        </div>
      )}

      <div className="flex gap-1 items-center flex-wrap justify-end mb-2">
        <input value={q} onChange={e => setQ(e.target.value)} placeholder="Search public-site queue…"
          className="h-7 px-2 rounded-lg border border-gray-200 text-[11px] w-44 focus:outline-none focus:border-[#C77D3F]" />
        {(['active', 'done', 'all'] as const).map(m => (
          <button key={m} onClick={() => setShow(m)}
            className={`h-7 px-2.5 rounded-full text-[11px] font-semibold border ${show === m ? 'bg-[#2C1810] text-white border-[#2C1810]' : 'border-gray-200 text-gray-500'}`}>
            {m === 'active' ? `Upcoming (${active.length})` : m === 'done' ? `Completed (${doneCount})` : 'All'}
          </button>
        ))}
      </div>

      {grouped.length === 0 ? (
        <p className="text-sm text-gray-400 py-2">
          {needle ? `Nothing in the public-site queue matches "${q}".`
            : show === 'done' ? 'Nothing completed on the public site yet.'
            : 'Nothing queued for ochrepoint.ca — file a request above.'}
        </p>
      ) : grouped.map(g => (
        <div key={g.st} className="mb-2 last:mb-0">
          <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-1 flex items-center gap-1">
            <span className={`material-symbols-outlined text-sm ${g.st === 'in_progress' ? 'animate-spin' : ''}`}>{STATUS_META[g.st]?.icon}</span>
            {STATUS_META[g.st]?.label} · {g.rows.length}
          </p>
          <div className="divide-y divide-gray-50">
            {g.rows.map(r => (
              <div key={r.id}>
                <button onClick={() => setOpenId(o => o === r.id ? null : r.id)}
                  className="w-full flex items-center gap-2 py-1.5 text-left hover:bg-gray-50 rounded px-1">
                  <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded-full ${STATUS_META[r.status]?.cls}`}>#{r.id}</span>
                  <span className="flex-1 min-w-0 text-sm text-gray-800 truncate">{r.title}</span>
                  {r.fulfill_now === 1 && r.status !== 'done' && <span className="text-[9px] font-bold text-[#C77D3F]">NOW</span>}
                  {r.priority === 'high' && <span className="text-[9px] font-bold text-red-600">HIGH</span>}
                  {r.element && <span className="text-[10px] text-gray-400 hidden sm:inline">{r.element}</span>}
                </button>
                {openId === r.id && (
                  <div className="pl-2 pb-2 pr-1 text-xs space-y-1.5">
                    {r.details && <p className="text-gray-600 whitespace-pre-wrap">{r.details}</p>}
                    {r.changelog ? (
                      <div>
                        <p className="text-[10px] font-bold uppercase tracking-wide text-gray-400 mb-0.5">Changelog</p>
                        <p className="text-gray-600 whitespace-pre-wrap bg-gray-50 rounded-lg p-2">{r.changelog}</p>
                      </div>
                    ) : (
                      <p className="text-gray-400 italic">No changelog yet — not fulfilled yet.</p>
                    )}
                    {r.status !== 'done' && r.status !== 'rejected' && (
                      <div className="flex gap-1.5 pt-0.5">
                        {r.fulfill_now !== 1 && (
                          <button onClick={() => runNow(r.id)}
                            className="h-7 px-2.5 rounded-lg bg-[#C77D3F] text-white text-[11px] font-bold">Fulfill now</button>
                        )}
                        {r.status === 'proposed' && (
                          <button onClick={() => setStatus(r.id, 'open')}
                            className="h-7 px-2.5 rounded-lg bg-[#5C7F4F] text-white text-[11px] font-bold">Approve → queue it</button>
                        )}
                        <button onClick={() => setStatus(r.id, 'rejected')}
                          className="h-7 px-2.5 rounded-lg border border-gray-200 text-gray-500 text-[11px] font-bold">Drop it</button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
